import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Center,
  Group,
  Paper,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { formatPhoneInput, isValidPhone } from '@blagodar/shared';
import { useAuth } from '../auth/AuthContext';
import { isNativeApp, needsApiSetup, NetworkError } from '../api/client';
import { nativePlatform } from '../api/platform';
import { describeApiError } from '../api/errors';
import { ServerSettingsModal } from '../components/ServerSettingsModal';
import { setDesignChoice, useWarmDesign } from '../design/design';

/**
 * Похожа ли строка на телефон.
 *
 * Смотрим на то, что НАБРАЛ человек: цифры, плюс и разделители — телефон;
 * появилась буква — логин. Без этого маска включалась бы на логине и
 * превращала «petrov» в «+7 (»: человек видит, что поле «портит» ввод, и
 * бросает.
 */
function looksLikePhone(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^[+\d][\d\s()+-]*$/.test(trimmed);
}

/**
 * Сколько держать палец на надписи «БлагоДар», чтобы открылось подключение
 * к серверу (правило 8). Дольше обычного долгого нажатия Android (~0,5 с):
 * случайно так не открыть, а руководителю полторы секунды не в тягость.
 */
const SERVER_HOLD_MS = 1500;

/** Долгое нажатие не должно выделять текст и звать меню WebView */
const HOLD_STYLE: CSSProperties = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
};

export function LoginPage() {
  const { login, loading } = useAuth();
  const warm = useWarmDesign();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  // Ошибку про номер показываем не на первой же букве, а когда человек
  // ушёл из поля: иначе красное появляется, едва начал набирать
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Вход не прошёл из-за СВЯЗИ — тогда рядом с отказом даётся путь к адресу
  // сервера: отказ, из которого не видно, что делать, — половина отказа
  // (правило 179)
  const [unreachable, setUnreachable] = useState(false);
  const holdTimer = useRef<number | null>(null);
  const native = isNativeApp();

  // В приложении на планшете без адреса сервера войти невозможно —
  // открываем настройку сразу, а не после неудачной попытки входа
  useEffect(() => {
    if (needsApiSetup()) setSettingsOpen(true);
  }, []);

  // Сам адрес на экране входа НЕ показывается (правило 8): планшет уходит
  // кладовщику и монтажникам, и строка «https://…/api» им ни к чему.
  // Сменить работающий адрес руководитель может, подержав палец на
  // надписи «БлагоДар» — дольше обычного нажатия, чтобы не открыть случайно
  const cancelHold = () => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };
  const startHold = () => {
    cancelHold();
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      setSettingsOpen(true);
    }, SERVER_HOLD_MS);
  };
  useEffect(() => {
    const timer = holdTimer;
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setUnreachable(false);
    setPhoneTouched(true);

    // Неполный НОМЕР проверяем до отправки: сервер отвечает на него
    // «Проверьте заполнение полей», и понять по этому ответу, что не так,
    // невозможно — а неполный номер самая частая причина неудачи.
    // Логин не проверяем: каким он бывает, знает только сервер
    if (looksLikePhone(phone) && !isValidPhone(phone)) {
      setError('Номер введён не полностью — проверьте логин. Пример: +7 (999) 000-00-00');
      return;
    }
    if (!phone.trim()) {
      setError('Введите телефон или логин');
      return;
    }
    if (!password) {
      setError('Введите пароль');
      return;
    }

    try {
      await login(phone, password);
    } catch (e) {
      if (e instanceof NetworkError) {
        // Первый вход обязан быть онлайн: без токена и справочников
        // работать оффлайн всё равно невозможно
        setError(
          native
            ? // Интернет есть, а приложение не пускают: на Honor и Xiaomi ему
              // могли однажды запретить передачу данных (12.10.2026), на
              // iPhone — выключить «Сотовые данные» для программы
              nativePlatform() === 'ios'
              ? 'Нет связи с сервером. Проверьте интернет. Если он есть — в Настройках iPhone ' +
                'откройте «БлагоДар» и включите «Сотовые данные».'
              : 'Нет связи с сервером. Проверьте интернет. Если он есть — в настройках телефона ' +
                'разрешите приложению БлагоДар мобильные данные и Wi‑Fi.'
            : 'Нет связи с сервером. Для первого входа нужен интернет.',
        );
        setUnreachable(true);
      } else {
        // Поля называются по-человечески, как и в остальных формах
        // (правило 179). Ответ на неверный вход при этом остаётся
        // одинаковым для «нет такого» и «неверный пароль» — его
        // формулирует сервер (правило 43)
        setError(describeApiError(e, 'Не удалось войти'));
      }
    }
  };

  return (
    <Center h="100vh" p="md">
      <Paper shadow="md" p="xl" radius="lg" w="100%" maw={420}>
        <form onSubmit={submit}>
          <Stack>
            <Title
              order={2}
              c="blagodar.8"
              onPointerDown={native ? startHold : undefined}
              onPointerUp={native ? cancelHold : undefined}
              onPointerLeave={native ? cancelHold : undefined}
              onPointerCancel={native ? cancelHold : undefined}
              onContextMenu={native ? (e) => e.preventDefault() : undefined}
              style={native ? HOLD_STYLE : undefined}
            >
              БлагоДар
            </Title>

            {error && (
              <Alert color="red" icon={<IconAlertCircle size={18} />}>
                <Stack gap={4}>
                  <span>{error}</span>
                  {unreachable && native && (
                    <Anchor size="sm" onClick={() => setSettingsOpen(true)}>
                      Проверить адрес сервера
                    </Anchor>
                  )}
                </Stack>
              </Alert>
            )}

            {/* Принимается И ТЕЛЕФОН, И ЛОГИН.
                Маска включается, только когда человек набирает ЦИФРЫ:
                иначе логин «petrov» превратился бы в «+7 (» на первой же
                букве. Что именно ввели, решает строка, а не вопрос
                человеку — спрашивать «вы вводите телефон или логин?»
                значит перекладывать на него нашу работу.

                Номер форматируется по мере ввода: раньше поле было
                обычным, и недобранную цифру не было видно — «+799955500»
                выглядит как номер, а сервер отвечал «Проверьте заполнение
                полей», не говоря, что именно. */}
            <TextInput
              label="Телефон или логин"
              placeholder="+7 (999) 000-00-00 или petrov"
              value={phone}
              onChange={(e) => {
                const next = e.currentTarget.value;
                setPhone(looksLikePhone(next) ? formatPhoneInput(next) : next);
              }}
              autoComplete="username"
              error={
                phoneTouched && looksLikePhone(phone) && !isValidPhone(phone)
                  ? 'Нужно 10 цифр после +7'
                  : undefined
              }
              onBlur={() => setPhoneTouched(true)}
              required
            />

            <PasswordInput
              label="Пароль"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="current-password"
              required
            />

            <Button type="submit" loading={loading} fullWidth size="lg" mt="sm">
              Войти
            </Button>

            {/* Адреса сервера здесь больше нет (правило 8): подключение
                открывается долгим нажатием на «БлагоДар», ссылкой при отказе
                связи и само — когда адреса нет вовсе. В браузере адрес —
                адрес страницы, и настраивать нечего */}
          </Stack>
        </form>
        {/* Вид — и до входа: планшет переходит из рук в руки, а новый вид
            по умолчанию только у руководства (правило 295) */}
        <Group justify="center" mt="sm">
          <Button
            variant="subtle"
            size="compact-sm"
            color="gray"
            onClick={() => setDesignChoice(warm ? 'classic' : 'warm')}
            data-testid="design-toggle-login"
          >
            {warm ? 'Вернуть прежний вид' : 'Новый вид'}
          </Button>
        </Group>
      </Paper>

      <ServerSettingsModal opened={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Center>
  );
}
